// Timeline — one row per JOB, days across the top, in ZT's gantt shape
// (round 22).
//
// Fixed 76px day columns over the anchor-independent window (today−30 to
// today+150), auto-scrolled on mount so today lands centred. One LANE PER
// BOOKING inside a job's row, and every bar is split per `daySegments` run so
// it visibly gaps over weekends and shutdowns instead of claiming the crew
// worked Sunday. Long bars repeat their label (`RepeatingLabel`) so the name
// stays under the eye five months in.
//
// One ZT bug deliberately not copied: its gantt's header name cell is not
// sticky-left, so the "Job" label scrolls away while the body column stays.
// Both halves are frozen here, like the resource boards.
//
// Maintenance stays off this board — it has no job (migration 013), and the
// Maintenance board is where a machine at the mechanic reads. Same call as ZT.
import { useEffect, useMemo, useRef } from 'react';
import {
  formatClockRange,
  monthLabel,
  todayISO,
  weekdayShort,
  type DateOnly,
} from '../../lib/dates';
import { jobColour } from '../../lib/jobColour';
import { nwShade, daySegments, type NwLookup, type WorksOn } from '../../lib/nonWorkingDays';
import { RepeatingLabel } from './RepeatingLabel';
import type { CalendarBooking } from '../../schedulingApi';

const DAY_W = 76;
const NAME_W = 160;
const LANE_H = 36;
const BAR_H = 28;

interface JobRow {
  jobId: string;
  jobNumber: string;
  jobTitle: string;
  inConflict: boolean;
  bookings: CalendarBooking[];
}

function toRows(bookings: CalendarBooking[]): JobRow[] {
  const map = new Map<string, JobRow>();

  for (const booking of bookings) {
    // One row per JOB. A maintenance booking has no job (migration 013) and
    // belongs on the Maintenance board; inventing a row for it here would say
    // a service is a job.
    if (booking.jobId === null) continue;

    const existing = map.get(booking.jobId);
    if (!existing) {
      map.set(booking.jobId, {
        jobId: booking.jobId,
        jobNumber: booking.jobNumber ?? '',
        jobTitle: booking.jobTitle ?? '',
        inConflict: booking.inConflict,
        bookings: [booking],
      });
      continue;
    }
    // One booking in trouble puts the whole job's chip in the conflict
    // colour — this view is scanned to find what needs attention.
    existing.inConflict ||= booking.inConflict;
    existing.bookings.push(booking);
  }

  return [...map.values()].sort((a, b) => a.jobNumber.localeCompare(b.jobNumber));
}

export function JobTimeline({
  bookings,
  days,
  onSelectBooking,
  nwInfo,
  worksOn,
}: {
  bookings: CalendarBooking[];
  days: DateOnly[];
  /** A bar is ONE booking, so a click is unambiguous — straight to edit. */
  onSelectBooking: (booking: CalendarBooking) => void;
  nwInfo: NwLookup;
  worksOn: WorksOn;
}) {
  const today = todayISO();
  const rows = useMemo(() => toRows(bookings), [bookings]);
  const from = days[0]!;
  const to = days[days.length - 1]!;

  const offsets = useMemo(() => new Map(days.map((d, i) => [d, i])), [days]);
  const totalW = NAME_W + days.length * DAY_W;

  // Centre today on mount — five months of columns with the reader parked at
  // day one would make the window's past half look like the whole board.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    const todayOffset = offsets.get(today);
    if (!el || todayOffset === undefined) return;
    el.scrollLeft = NAME_W + todayOffset * DAY_W - el.clientWidth / 2 + DAY_W / 2;
  }, [offsets, today]);

  if (rows.length === 0) {
    return (
      <div className="card p-10 text-center">
        <p className="text-sm font-medium text-gray-900">Nothing scheduled in this window</p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500">
          Book something, or clear the filters above.
        </p>
      </div>
    );
  }

  const isMonday = (date: DateOnly) => new Date(`${date}T00:00:00`).getDay() === 1;

  return (
    <div className="card overflow-hidden">
      <div ref={scrollRef} style={{ overflow: 'auto', maxHeight: '75vh' }}>
        <div style={{ width: totalW, minWidth: '100%' }}>
          {/* Header — frozen on BOTH axes at the corner. */}
          <div className="sticky top-0 z-10 flex border-b border-gray-200 bg-gray-50">
            <div
              style={{ width: NAME_W, minWidth: NAME_W }}
              className="sticky left-0 z-20 flex-none border-r border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
            >
              Job
            </div>
            <div className="grid" style={{ gridTemplateColumns: `repeat(${days.length}, ${DAY_W}px)` }}>
              {days.map((date) => {
                const nw = nwInfo(date);
                return (
                  <div
                    key={date}
                    className={`border-r border-gray-200 py-1.5 text-center ${
                      date === today ? 'bg-brand-100' : nwShade(nw, true)
                    } ${isMonday(date) ? 'border-l-2 border-l-gray-300' : ''}`}
                  >
                    <div className="text-xs font-medium leading-none text-gray-500">
                      {weekdayShort(date)[0]}
                    </div>
                    <div
                      className={`text-sm font-bold leading-tight ${
                        date === today ? 'text-brand-800' : 'text-gray-700'
                      }`}
                    >
                      {Number(date.slice(8, 10))}
                    </div>
                    <div className="text-[10px] leading-none text-gray-400">
                      {monthLabel(date).slice(0, 3)}
                    </div>
                    {nw?.dated && (
                      <div
                        className="mt-0.5 truncate px-0.5 text-[9px] font-medium leading-none text-red-700"
                        title={nw.name ?? undefined}
                      >
                        {nw.name}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {rows.map((row) => {
            const rowHeight = Math.max(1, row.bookings.length) * LANE_H + 8;
            const colour = jobColour(row.jobNumber, row.jobId, row.inConflict);

            return (
              <div key={row.jobId} className="flex border-b border-gray-100">
                <div
                  style={{ width: NAME_W, minWidth: NAME_W }}
                  className="sticky left-0 z-[5] flex flex-none flex-col justify-center border-r border-gray-200 bg-white px-3 py-2"
                >
                  <div
                    className={`mb-0.5 inline-block truncate rounded px-1.5 py-0.5 text-xs font-bold ${colour.bar} ${colour.barText}`}
                    title={row.jobTitle}
                  >
                    {row.jobTitle}
                  </div>
                  <div className="truncate font-mono text-xs text-gray-500">{row.jobNumber}</div>
                </div>

                <div className="relative" style={{ width: days.length * DAY_W, height: rowHeight }}>
                  {/* Column backdrops: today, non-working, Monday rules. */}
                  <div className="pointer-events-none absolute inset-0 flex">
                    {days.map((date) => {
                      const nw = nwInfo(date);
                      return (
                        <div
                          key={date}
                          style={{ width: DAY_W }}
                          className={`h-full border-r border-gray-100 ${
                            date === today ? 'bg-brand-50/60' : nwShade(nw)
                          } ${isMonday(date) ? 'border-l border-l-gray-200' : ''}`}
                        />
                      );
                    })}
                  </div>

                  {/* One lane per booking; bars split per worked-day run. */}
                  {row.bookings.map((booking, laneIndex) =>
                    daySegments(booking, from, to, worksOn).map((segment) => {
                      const segOffset = offsets.get(segment.start)!;
                      const segSpan = offsets.get(segment.end)! - segOffset + 1;
                      const label = booking.resourceName;
                      const times = formatClockRange(booking.startTime, booking.endTime);
                      return (
                        <button
                          key={`${booking.id}:${segment.start}`}
                          type="button"
                          onClick={() => onSelectBooking(booking)}
                          title={`${label} · ${row.jobNumber} · ${times ?? 'all day'}${
                            booking.note ? ` · ${booking.note}` : ''
                          }${booking.inConflict ? ', needs re-planning' : ''}`}
                          style={{
                            position: 'absolute',
                            left: segOffset * DAY_W,
                            width: segSpan * DAY_W,
                            top: laneIndex * LANE_H + 4,
                            height: BAR_H,
                          }}
                          className={`bar-clip flex items-center rounded text-xs font-semibold transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-brand-600 ${colour.bar} ${colour.barText}`}
                        >
                          <RepeatingLabel
                            width={segSpan * DAY_W}
                            labelWidth={label.length * 6.8}
                            stickyLeft={NAME_W}
                          >
                            {label}
                          </RepeatingLabel>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
